import { KoobCard } from '@koob/ui';
import KoobTitle from '../../../components/Koob/KoobTitle';
import { useTranslation } from 'react-i18next';
import KoobParagraph from '../../../components/Koob/KoobParagraph';
import { useAsync } from 'react-async';
import {
  getHotelAllotments,
  updateHotelAllotments,
} from '../../../api/node/hotels';
import { dayjs, toDateString } from '@koob/dates';
import { useEffect, useMemo, useRef, useState } from 'react';
import AllotmentsEditorMonthSelector, {
  AllotmentButton,
} from './AllotmentsEditorMonthSelector';
import AllotmentsEditorBody from './AllotmentsEditorBody';
import { Form, Formik } from 'formik';
import AllotmentsEditorLegend from './AllotmentsEditorLegend';
import { useSnack } from '../../../hooks';
import { removeUntouchedValues } from './allotmentsUtils';

export default function AllotmentsEditor({ hotel }) {
  const { t } = useTranslation('allotments');
  const { notify } = useSnack();
  const formRef = useRef(null);

  const [previousHotelId, setPreviousHotelId] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs().month());
  const [selectedDays, setSelectedDays] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, reload } = useAsync({
    promiseFn: getHotelAllotments,
    hotelId: hotel.id,
    from: toDateString(dayjs().month(currentMonth).startOf('month')),
    to: toDateString(dayjs().month(currentMonth).endOf('month')),
    watchFn: (prevProps, nextProps) => {
      return (
        prevProps.from !== nextProps.from ||
        prevProps.to !== nextProps.to ||
        prevProps.hotelId !== nextProps.hotelId
      );
    },
  });
  const initialValues = { rooms: data?.data };

  useEffect(() => {
    if (initialValues && (!previousHotelId || previousHotelId !== hotel.id)) {
      resetForm();
      setPreviousHotelId(hotel.id);
    }
  }, [initialValues]);

  const daysInMonth = useMemo(() => {
    return dayjs().month(currentMonth).daysInMonth();
  }, [currentMonth]);

  const submitForm = (values) => {
    setSubmitting(true);

    const filteredValues = removeUntouchedValues(values);

    updateHotelAllotments({
      hotelId: hotel.id,
      payload: filteredValues,
    })
      .then(() => {
        notify(t('editor.saveSuccess'));
        resetForm();
      })
      .catch((error) => {
        if (error?.response?.data?.message) {
          notify(error?.response?.data?.message, { type: 'error' });
        } else {
          notify(t('misc:saveError'), { type: 'error' });
        }
      })
      .finally(() => {
        setSubmitting(false);
        reload();
      });
  };

  const resetForm = () => {
    formRef.current.setValues(initialValues);
    setSelectedDays([]);
  };

  return (
    <KoobCard>
      <KoobTitle>{t('editor.title')}</KoobTitle>
      <KoobParagraph>
        {t('editor.description')}
        <br />
        {t('editor.changeMonthWarning')}
      </KoobParagraph>

      <div className="mt-5 flow-root overflow-hidden">
        <AllotmentsEditorMonthSelector
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          reload={reload}
          isDisabled={selectedDays?.length > 0}
        />

        <Formik
          initialValues={initialValues}
          onSubmit={(values) => submitForm(values.rooms)}
          innerRef={formRef}
          enableReinitialize
        >
          <Form>
            <AllotmentsEditorBody
              initialValues={initialValues}
              loading={isLoading}
              currentMonth={currentMonth}
              daysInMonth={daysInMonth}
              selectedDays={selectedDays}
              setSelectedDays={setSelectedDays}
            />

            <div className="mt-3 flex justify-between">
              <AllotmentButton
                onClick={() => resetForm()}
                disabled={isLoading || submitting}
              >
                <i className="far fa-refresh mr-1.5" />
                {t('misc:reset')}
              </AllotmentButton>

              <AllotmentButton
                type="submit"
                testId="submit-change"
                disabled={isLoading || submitting}
              >
                {t('misc:save')}
                <i className="far fa-save ml-1.5" />
              </AllotmentButton>
            </div>

            <div className="mt-3">
              <AllotmentsEditorLegend />
            </div>
          </Form>
        </Formik>
      </div>
    </KoobCard>
  );
}
